import { EResult, ESuiteApiResult } from './types';

/**
 * Error kinds surfaced by the API client. Kept coarse so the UI can react
 * without depending on transport details.
 */
export type ApiErrorKind =
  | 'network' // request never reached the server (offline, DNS, TLS, ...)
  | 'unauthorized' // credentials rejected / session no longer valid
  | 'server' // Portal returned a non-success EResult or HTTP 5xx
  | 'aborted' // request cancelled via AbortSignal
  | 'unknown';

/**
 * Stable, translatable identifiers for the failure modes the UI distinguishes.
 * The API layer returns a key; the React layer resolves it through the `errors`
 * message namespace, so no untranslated English text can reach the user.
 */
export type ApiErrorKey =
  | 'invalidCredentials'
  | 'invalidResetLink'
  | 'emailAlreadyUsed'
  | 'accountLocked'
  | 'accountNotActivated'
  | 'noLicense'
  | 'alreadySignedIn'
  | 'accessDenied'
  | 'outdatedClient'
  | 'serverUnavailable'
  | 'invalidResponse'
  | 'fileRejected'
  | 'storageFull'
  | 'fileAlreadyUploaded'
  | 'uploadRequestInvalid'
  | 'weakPassword'
  | 'missingFields'
  | 'network'
  | 'aborted'
  | 'sessionExpired'
  | 'generic';

/**
 * Normalised error thrown by the API client.
 *
 * SECURITY: nothing user-supplied, no credentials, tokens or payloads are placed
 * on this error. `message` is a short technical marker for developer tooling —
 * the UI renders the translation of {@link key} instead.
 */
export class ApiError extends Error {
  public readonly kind: ApiErrorKind;
  public readonly key: ApiErrorKey;
  public readonly serverResult?: EResult | ESuiteApiResult;
  public readonly httpStatus?: number;

  public constructor(
    kind: ApiErrorKind,
    key: ApiErrorKey,
    options?: { serverResult?: EResult | ESuiteApiResult; httpStatus?: number; cause?: unknown },
  ) {
    super(`${kind}/${key}`, options?.cause !== undefined ? { cause: options.cause } : undefined);
    this.name = 'ApiError';
    this.kind = kind;
    this.key = key;
    if (options?.serverResult !== undefined) {
      this.serverResult = options.serverResult;
    }
    if (options?.httpStatus !== undefined) {
      this.httpStatus = options.httpStatus;
    }
  }

  public get isUnauthorized(): boolean {
    return this.kind === 'unauthorized';
  }
}

/** Resolves the translatable error key of any thrown value. */
export function errorKeyOf(error: unknown): ApiErrorKey {
  return error instanceof ApiError ? error.key : 'generic';
}

/** Maps a Portal EResult (login/logout contract) to a translatable error key. */
export function errorKeyForResult(result: EResult): ApiErrorKey {
  switch (result) {
    case EResult.InvalidLogin:
    case EResult.InvalidPassword:
      return 'invalidCredentials';
    case EResult.NotUnique:
      return 'emailAlreadyUsed';
    case EResult.UserLocked:
      return 'accountLocked';
    case EResult.UserNotActivated:
      return 'accountNotActivated';
    case EResult.NoLicense:
      return 'noLicense';
    case EResult.AlreadyLoggedIn:
      return 'alreadySignedIn';
    case EResult.AccessDenied:
      return 'accessDenied';
    case EResult.IncorrectVersion:
      return 'outdatedClient';
    case EResult.NoServer:
    case EResult.ServerError:
      return 'serverUnavailable';
    default:
      return 'generic';
  }
}

/**
 * Maps a SuiteAPI EResult (Dental.Portal.Areas.SuiteAPI.Models.EResult) to a
 * translatable error key. Distinct from {@link errorKeyForResult} — the two
 * result-code contracts have unrelated numeric values, see `ESuiteApiResult`.
 */
export function errorKeyForSuiteApiResult(result: ESuiteApiResult): ApiErrorKey {
  switch (result) {
    case ESuiteApiResult.InvalidLogin:
      return 'invalidCredentials';
    case ESuiteApiResult.InvalidToken:
      return 'invalidResetLink';
    case ESuiteApiResult.NoLicense:
      return 'noLicense';
    case ESuiteApiResult.UserLocked:
      return 'accountLocked';
    case ESuiteApiResult.UserNotActivated:
      return 'accountNotActivated';
    case ESuiteApiResult.AlreadyLoggedIn:
      return 'alreadySignedIn';
    case ESuiteApiResult.IncorrectVersion:
      return 'outdatedClient';
    case ESuiteApiResult.NoConnection:
      return 'serverUnavailable';
    case ESuiteApiResult.NoSpace:
      return 'storageFull';
    case ESuiteApiResult.BadRequest:
      return 'uploadRequestInvalid';
    case ESuiteApiResult.FilePending:
      return 'fileAlreadyUploaded';
    case ESuiteApiResult.AccessDenied:
      return 'accessDenied';
    case ESuiteApiResult.NotUnique:
      return 'emailAlreadyUsed';
    case ESuiteApiResult.InvalidPassword:
      return 'weakPassword';
    case ESuiteApiResult.NotValid:
      return 'missingFields';
    case ESuiteApiResult.ServerError:
      return 'serverUnavailable';
    default:
      return 'generic';
  }
}
